import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { congesApi } from '../../services/conges-api';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Champ,
  SaisieNombre,
  Section,
  Selection,
  ZoneTexte,
} from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';
import {
  CarteCompteur,
  ChronologieCompteurs,
  LIBELLE_COMPTEUR,
  jours,
} from '../../components/conges/Compteurs';

/**
 * R-CPT-14 : la vue RH des compteurs.
 *
 * C'est le tableau de pilotage quotidien, celui qui remplace Airtable. Il
 * répond d'un coup d'œil à « qui est dans le rouge » et « qui a beaucoup de
 * reliquat à poser », puis mène à la chronologie d'un salarié, seul endroit où
 * un solde s'explique et se corrige.
 */
export function CongesCompteursPage() {
  const [selection, setSelection] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'compteurs'],
    queryFn: () => congesApi.demandes.tousLesCompteurs(),
  });

  if (isLoading) return <p className="p-4 text-nuit/60">Chargement…</p>;
  if (error) return <MessageErreur erreur={error} />;

  const salaries = data?.salaries ?? [];
  if (selection) {
    const ligne = salaries.find((s) => s.salarie_id === selection);
    return (
      <DetailSalarie
        salarieId={selection}
        nom={ligne?.nom ?? ''}
        onRetour={() => setSelection(null)}
      />
    );
  }

  // Les colonnes sont l'union des compteurs ouverts : un 35 heures n'a pas de
  // RTT, mais la colonne doit exister si quelqu'un d'autre en a.
  const colonnes: string[] = [];
  for (const s of salaries) {
    for (const c of s.compteurs) if (!colonnes.includes(c.cle)) colonnes.push(c.cle);
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <header>
        <h1 className="font-titre text-xl font-bold text-nuit">Compteurs</h1>
        <p className="text-sm text-nuit/60">
          Tous les salariés concernés par les congés — y compris ceux dont la fiche ne permet pas
          encore d’ouvrir des compteurs, signalés par leur motif. Chaque compteur se lit en trois temps :
          le solde d’aujourd’hui, ce qui est en attente de validation, et le prévisionnel.
          Cliquez sur une ligne pour voir d’où vient un solde et le corriger.
        </p>
      </header>

      {salaries.length === 0 ? (
        <p className="rounded-lg border border-nuit/10 bg-white p-4 text-sm text-nuit/60">
          Aucun salarié concerné par les congés pour l’instant.
        </p>
      ) : (
        <>
          {/* Bureau : le tableau, qui permet la comparaison entre salariés. */}
          <div className="hidden overflow-x-auto rounded-lg border border-nuit/10 bg-white md:block">
            <table className="min-w-full text-sm">
              <thead className="bg-ciel/20 text-left text-nuit/70">
                <tr>
                  <th className="px-3 py-2 font-medium">Salarié</th>
                  {colonnes.map((cle) => (
                    <th key={cle} className="px-3 py-2 text-right font-medium">
                      {LIBELLE_COMPTEUR[cle] ?? cle}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {salaries.map((s) => (
                  <tr
                    key={s.salarie_id}
                    onClick={() => setSelection(s.salarie_id)}
                    className="cursor-pointer border-t border-nuit/10 hover:bg-ciel/15"
                  >
                    <td className="px-3 py-2 font-medium text-nuit">
                      {s.nom}
                      {s.motif_exclusion && (
                        <span className="block text-xs font-normal text-jaune">{s.motif_exclusion}</span>
                      )}
                    </td>
                    {colonnes.map((cle) => {
                      // Une fiche sans compteurs possibles n'affiche pas « 0,00 j » :
                      // ce zéro se lit comme un solde épuisé, alors que la fiche
                      // est seulement incomplète. Le motif est à gauche.
                      if (s.motif_exclusion) return <td key={cle} className="px-3 py-2 text-right text-nuit/30">—</td>;
                      const c = s.compteurs.find((x) => x.cle === cle);
                      if (!c) return <td key={cle} className="px-3 py-2 text-right text-nuit/30">-</td>;
                      return (
                        <td key={cle} className="px-3 py-2 text-right">
                          <span
                            className={`tabular-nums ${
                              c.previsionnel < 0 ? 'font-semibold text-corail' : 'text-nuit'
                            }`}
                          >
                            {jours(c.previsionnel)} j
                          </span>
                          {c.en_attente > 0 && (
                            <span className="block text-xs text-jaune">
                              dont {jours(c.en_attente)} j en attente
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile : une carte par salarié, jamais un tableau à faire glisser. */}
          <div className="space-y-3 md:hidden">
            {salaries.map((s) => (
              <button
                key={s.salarie_id}
                type="button"
                onClick={() => setSelection(s.salarie_id)}
                className="block w-full rounded-lg border border-nuit/10 bg-white p-3 text-left"
              >
                <p className="font-medium text-nuit">{s.nom}</p>
                {s.motif_exclusion && <p className="mt-1 text-xs text-jaune">{s.motif_exclusion}</p>}
                <dl className="mt-2 space-y-1 text-sm">
                  {(s.motif_exclusion ? [] : s.compteurs).map((c) => (
                    <div key={c.cle} className="flex justify-between">
                      <dt className="text-nuit/60">{LIBELLE_COMPTEUR[c.cle] ?? c.cle}</dt>
                      <dd
                        className={`tabular-nums ${
                          c.previsionnel < 0 ? 'font-semibold text-corail' : 'text-nuit'
                        }`}
                      >
                        {jours(c.previsionnel)} j
                      </dd>
                    </div>
                  ))}
                </dl>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** La chronologie d'un salarié, et la correction manuelle (R-VAL-11). */
function DetailSalarie({
  salarieId,
  nom,
  onRetour,
}: {
  salarieId: string;
  nom: string;
  onRetour: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [correction, setCorrection] = useState(false);
  const [compteur, setCompteur] = useState('CP_N');
  const [nombre, setNombre] = useState<number | null>(null);
  const [motif, setMotif] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'compteurs', salarieId],
    queryFn: () => congesApi.demandes.compteurs(salarieId),
  });

  const corriger = useMutation({
    mutationFn: () =>
      congesApi.demandes.corriger(salarieId, {
        compteur,
        jours: nombre ?? 0,
        commentaire: motif,
      }),
    onSuccess: () => {
      toast.success('Compteur corrigé.');
      setCorrection(false);
      setNombre(null);
      setMotif('');
      qc.invalidateQueries({ queryKey: ['conges', 'compteurs'] });
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'La correction a échoué.'),
  });

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 md:p-6">
      {/* Le retour est en TÊTE : sinon il faut dérouler toute la chronologie
          pour revenir au tableau. */}
      <button type="button" onClick={onRetour} className="text-sm text-nuit/70 hover:text-nuit">
        ← Retour
      </button>

      <h1 className="font-titre text-xl font-bold text-nuit">{nom}</h1>

      {isLoading && <p className="text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      {data && (
        <>
          <Section titre="Chronologie des compteurs" teinte="ciel">
            <ChronologieCompteurs mouvements={data.mouvements} avecAuteur />
          </Section>

          <Section titre="Corriger un compteur" teinte="jaune">
            {!correction ? (
              <>
                <p className="mb-3 text-sm text-nuit/70">
                  Une correction s’ajoute à l’histoire du compteur, elle ne la réécrit pas. Le motif
                  est obligatoire : c’est lui qui permettra de défendre le solde plus tard.
                </p>
                <BoutonSecondaire type="button" onClick={() => setCorrection(true)}>
                  Corriger un compteur
                </BoutonSecondaire>
              </>
            ) : (
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  corriger.mutate();
                }}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <Champ label="Compteur">
                    <Selection value={compteur} onChange={(e) => setCompteur(e.target.value)}>
                      {Object.entries(LIBELLE_COMPTEUR).map(([cle, libelle]) => (
                        <option key={cle} value={cle}>
                          {libelle}
                        </option>
                      ))}
                    </Selection>
                  </Champ>
                  <Champ label="Jours (négatif pour retirer)">
                    <SaisieNombre valeur={nombre} onValeur={setNombre} nullable step="0.5" />
                  </Champ>
                </div>
                <Champ label="Motif">
                  <ZoneTexte value={motif} onChange={(e) => setMotif(e.target.value)} />
                </Champ>
                <div className="flex flex-wrap gap-2">
                  <BoutonPrincipal
                    type="submit"
                    disabled={corriger.isPending || !motif.trim() || !nombre}
                  >
                    Enregistrer la correction
                  </BoutonPrincipal>
                  <BoutonSecondaire type="button" onClick={() => setCorrection(false)}>
                    Annuler
                  </BoutonSecondaire>
                </div>
              </form>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
